/**
 * ChatInput Component - Campo de entrada do chat
 * @module features/chat/components/ChatInput
 *
 * Textarea expansivel com botao de envio para o chat.
 * Suporta Enter para enviar e Shift+Enter para quebra de linha.
 *
 * T002-FE: Interface de Chat Conversacional
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Send } from 'lucide-react';
import {
  type ChangeEvent,
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import type { ChatInputProps } from '../types/chat.types';

/**
 * Handle para controlar o foco do input externamente
 */
export interface ChatInputHandle {
  focus: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Campo de entrada para mensagens do chat
 *
 * Features:
 * - Enter envia, Shift+Enter quebra linha
 * - Expansao ate 4 linhas
 * - Botao desabilitado quando vazio ou loading
 * - Estados visuais de loading
 * - Acessibilidade completa
 *
 * @example
 * ```tsx
 * <ChatInput
 *   value={input}
 *   onChange={setInput}
 *   onSubmit={handleSubmit}
 *   disabled={isLoading}
 * />
 * ```
 */
export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  { value, onChange, onSubmit, disabled = false, placeholder = 'Digite sua mensagem...' },
  ref
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isValueEmpty = !value || value.trim().length === 0;
  const canSubmit = !isValueEmpty && !disabled;

  // Expor metodo focus via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    },
  }));

  /**
   * Handler de teclas - Enter envia, Shift+Enter quebra linha
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSubmit) {
          onSubmit();
        }
      }
      // Shift+Enter permite quebra de linha naturalmente
    },
    [canSubmit, onSubmit]
  );

  /**
   * Handler de mudanca no textarea
   */
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  /**
   * Handler do botao de submit
   */
  const handleButtonClick = useCallback(() => {
    if (canSubmit) {
      onSubmit();
    }
  }, [canSubmit, onSubmit]);

  return (
    <div
      data-testid="chat-input-container"
      className={cn(
        'flex items-end gap-2 p-2',
        'border border-input rounded-lg',
        'bg-background',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'
      )}
    >
      <textarea
        ref={textareaRef}
        value={value ?? ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={1}
        aria-label="Digite sua mensagem"
        aria-disabled={disabled}
        className={cn(
          'flex-1 min-h-[40px] max-h-24',
          'px-3 py-2',
          'bg-transparent',
          'text-foreground placeholder:text-muted-foreground',
          'resize-none overflow-y-auto',
          'focus:outline-none focus:ring-0',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
        style={{
          // Auto-grow textarea
          height: 'auto',
          minHeight: '40px',
        }}
      />

      <Button
        type="button"
        size="icon"
        onClick={handleButtonClick}
        disabled={!canSubmit}
        aria-label="Enviar mensagem"
        className={cn(
          'shrink-0',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        )}
      >
        {disabled ? (
          <>
            <Loader2
              data-testid="loader-icon"
              className="h-4 w-4 animate-spin"
              aria-hidden="true"
            />
            <span data-testid="button-loading-indicator" className="sr-only">
              Enviando...
            </span>
          </>
        ) : (
          <Send data-testid="send-icon" className="h-4 w-4" aria-hidden="true" />
        )}
      </Button>
    </div>
  );
});

export default ChatInput;
